import type { Appointment } from '../api/client'
import type { Discount } from '../types/app'
import { formatPrice } from '../catalog/format'

// discountRow is the line under the price: "AUTUMN10 −£8.50", "Friend −£10",
// or "20% off −£17" when the discount has no name. Null when there is none.
export function discountRow(a: Appointment): string | null {
  const d = a.discount
  if (!d) return null
  const name = d.label ?? (d.kind === 'percent' ? `${d.value}% off` : `${formatPrice(d.value)} off`)
  return `${name} −${formatPrice(d.pence)}`
}

// canTakeDiscount: not cancelled or no-show, not a live deposit hold, and not
// completed with nothing left to pay. Mirrors the server's 409 appointment-state.
export function canTakeDiscount(a: Appointment): boolean {
  if (a.status === 'cancelled' || a.status === 'no_show') return false
  if (a.awaiting_deposit) return false
  if (a.status === 'completed' && a.owed_pence <= 0) return false
  return true
}

const reasons: Record<string, string> = {
  unknown: "This code isn’t recognised.",
  ended: 'This code has ended.',
  not_started: "This code hasn’t started yet.",
  service: "This code doesn’t cover this service.",
  used_up: 'This code has been fully used.',
  used_before: "You’ve used this code before.",
}

// discountProblem is the words for a refused discount. The server's own detail
// wins for a code (it knows the date and the service); the table is the fallback.
export function discountProblem(p: { problem: string; reason?: string; detail?: string }, paidPence = 0): string {
  if (p.problem === 'discount-code') {
    return p.detail || (p.reason && reasons[p.reason]) || "This code can’t be used."
  }
  if (p.problem === 'discount-below-paid') return `${formatPrice(paidPence)} has already been paid. To give money back, refund instead.`
  if (p.problem === 'discount-unavailable') return p.detail || "That discount can’t be used any more."
  return 'Something went wrong. Try again.'
}

export interface Sale {
  offer: Discount
  pence: number
}

// saleFor is the live sale a new booking of this length will get, worked out
// the way the server does: percent rounds half up, an amount never exceeds the
// price, the larger wins, a tie goes to the earlier start then the lower id.
// today is the local date, YYYY-MM-DD.
// length is the service length being booked: its id and price.
export function saleFor(length: { id: string; price_pence: number }, offers: Discount[], today: string): Sale | null {
  let best: Sale | null = null
  for (const offer of offers) {
    if (offer.code !== null || offer.ended_at !== null) continue
    if (offer.starts_on > today || (offer.ends_on !== null && offer.ends_on < today)) continue
    if (!offer.all_services && !offer.service_length_ids.includes(length.id)) continue
    const sum = length.price_pence
    const pence = offer.kind === 'percent' ? Math.floor((sum * offer.value + 50) / 100) : Math.min(offer.value, sum)
    if (
      !best ||
      pence > best.pence ||
      (pence === best.pence &&
        (offer.starts_on < best.offer.starts_on || (offer.starts_on === best.offer.starts_on && offer.id < best.offer.id)))
    ) {
      best = { offer, pence }
    }
  }
  return best
}
